import React, { useState, useEffect, useRef } from 'react';
import { Chart } from 'chart.js/auto';
import ImageGallery from './ImageGallery';
import PropertyDocumentsPanel from './PropertyDocumentsPanel';
import PropertyLocationMap from '../shared/PropertyLocationMap';
import ListingAgentProfileCard from '../agent/ListingAgentProfileCard';
import RentMonthlyCostsBreakdown from '../charts/RentMonthlyCostsBreakdown';
import SaleInvestmentCostBreakdown from '../charts/SaleInvestmentCostBreakdown';
import CountryFlag from '../shared/CountryFlag';
import {
  getPropertyListingMode,
  getPropertyPhotoUrls,
  getPropertyListedAt,
  formatPropertyDateTime,
  parsePropertyTimestamp,
  getEffectiveMinInvestment,
  formatOfferingField,
  formatSecOfferingType,
  propertyManagerFromRecord,
  resolveListingAgentFields,
  hasListingAgent,
} from '../../utils/properties';
import { getRentMonthlyCostItems, getRentAllInMonthly, getNearbyEntries } from '../../utils/submissions';
import {
  formatExactMoney,
  formatExactShareMoney,
  getDisplayCountry,
  getCountry,
  normalizeCountryIso,
  isMultiMarketView,
  estimateInvestmentTotalUsd,
} from '../../data/countries';
import { PROPERTY_OFFERING_DISCLAIMER } from '../../config/constants';
import { getSharesOwnedForProperty, formatOwnedSharesMessage } from '../../utils/investments';

const formatPercent = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `${n}%` : '—';
};

const PropertyDetail = ({
  property,
  onBack,
  onInvest,
  userBalance,
  investments,
  country,
  isFavorite,
  onToggleFav,
  onContactAgent,
  isLoggedIn,
  addToast,
  onSignIn,
}) => {
  const [tab, setTab] = useState("overview");
  const isRentalListing = getPropertyListingMode(property) === "rent";
  const pricePerShare = property.price / property.shares;
  const sharesSold = property.sharesSold || 0;
  const progress = property.shares ? (sharesSold / property.shares) * 100 : 0;
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const myShares = getSharesOwnedForProperty(investments, property.id);
  const ownedSharesMessage = formatOwnedSharesMessage(myShares);
  const c = getDisplayCountry(country);
  const propCountryMeta = getCountry(normalizeCountryIso(property.country || "US"));

  const monthlyRent = Number(property.monthlyRent) || 0;
  const annualRent = monthlyRent > 0 ? monthlyRent * 12 : null;
  const occupancy = property.occupancy != null && property.occupancy !== '' ? Number(property.occupancy) : null;
  const annualExpenses = property.annualOperatingExpenses != null && property.annualOperatingExpenses !== ''
    ? Number(property.annualOperatingExpenses)
    : null;
  const noi = property.netOperatingIncome != null && property.netOperatingIncome !== ''
    ? Number(property.netOperatingIncome)
    : null;
  const capRate = property.capRate != null && property.capRate !== '' ? Number(property.capRate) : null;
  const projectedReturn = property.projectedReturn != null && property.projectedReturn !== ''
    ? Number(property.projectedReturn)
    : null;
  const hasAdminFinancials = noi != null || capRate != null || projectedReturn != null;
  const listPrice = Number(property.price) || 0;

  useEffect(() => {
    if (tab !== "financials" || !chartRef.current || !hasAdminFinancials) return undefined;
    chartInstanceRef.current?.destroy();
    const ctx = chartRef.current.getContext("2d");
    const base = listPrice > 0 ? listPrice : (noi && capRate ? (noi / (capRate / 100)) : 0);
    const growth = projectedReturn ? projectedReturn / 100 : 0;
    const points = base > 0
      ? [0, 1, 2, 3, 4].map((i) => Math.round(base * Math.pow(1 + growth, i)))
      : [];
    chartInstanceRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
        datasets: [{
          label: projectedReturn != null ? "CoOwn reviewed projection" : "Value projection",
          data: points.length ? points : [0, 0, 0, 0, 0],
          borderColor: "#102734",
          backgroundColor: "rgba(16,39,52,0.12)",
          fill: true,
          tension: 0.15,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom" } },
      },
    });
    return () => {
      chartInstanceRef.current?.destroy();
      chartInstanceRef.current = null;
    };
  }, [tab, listPrice, noi, capRate, projectedReturn, hasAdminFinancials]);

  return (
    <div className="container" style={{paddingTop:"2rem"}}>
      <button className="btn btn-ghost" onClick={onBack} style={{marginBottom:"1rem"}}>
        <i className="fas fa-arrow-left"></i> Back to Properties
      </button>

      <ImageGallery images={getPropertyPhotoUrls(property)} video={property.video} title={property.title} />

      <div style={{marginTop:"1rem",marginBottom:"1.5rem"}}>
        <span className="property-badge" style={{position:"static",display:"inline-block",marginRight:"0.5rem",...(isRentalListing?{background:"var(--info)"}:{})}}>
          {isRentalListing ? "For rent" : property.status === "funded" ? "Fully Funded" : "Accepting Investments"}
        </span>
        {property.landBanking && <span className="property-badge land" style={{position:"static",display:"inline-block",marginRight:"0.5rem"}}>Land Banking</span>}
        <span className="property-badge" style={{position:"static",display:"inline-flex",alignItems:"center",gap:"0.35rem",background:"var(--info)",marginRight:"0.5rem"}}>
          <CountryFlag code={property.country || "US"} imgWidth={18} emojiSize="1.1rem" />
          {isMultiMarketView(country) ? propCountryMeta.name : c.name}
        </span>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
          <h1 style={{ marginTop: "0.5rem", fontSize: "2rem", marginBottom: 0, flex: 1 }}>{property.title}</h1>
          {onToggleFav && (
            <button
              type="button"
              className={`btn btn-outline btn-sm${isFavorite ? " active" : ""}`}
              style={isFavorite ? { borderColor: "var(--danger)", color: "var(--danger)", background: "rgba(220,53,69,0.08)" } : undefined}
              onClick={() => onToggleFav(property.id)}
              title={isFavorite ? "Remove from favorites" : "Save to favorites"}
            >
              <i className={`${isFavorite ? "fas" : "far"} fa-heart`}></i> {isFavorite ? "Saved" : "Save"}
            </button>
          )}
        </div>
        <p style={{color:"var(--text-light)"}}>
          <i className="fas fa-map-marker-alt" style={{color:"var(--accent)"}}></i> {property.location}
          &nbsp;|&nbsp; Built {property.yearBuilt || "N/A"}
        </p>
        {getPropertyListedAt(property) && (
          <p className="property-listed-at" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
            <i className="fas fa-clock" aria-hidden="true"></i>
            Listed on site {formatPropertyDateTime(getPropertyListedAt(property))}
            {parsePropertyTimestamp(property.submittedAt) && (
              <span style={{ marginLeft: "0.35rem", opacity: 0.85 }}>
                · Submitted {formatPropertyDateTime(parsePropertyTimestamp(property.submittedAt))}
              </span>
            )}
          </p>
        )}
        <p className="property-price" style={{ fontSize: "1.75rem", marginTop: "0.65rem", marginBottom: 0 }}>
          {isRentalListing ? (
            <>
              <span className="property-price-label" style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, color: "var(--text-light)", marginBottom: "0.25rem" }}>Monthly rent</span>
              {monthlyRent > 0 ? `${formatExactMoney(property.monthlyRent, country)}/month` : "Contact for rate"}
            </>
          ) : (
            formatExactMoney(property.price, country)
          )}
        </p>
      </div>

      {!isRentalListing && (
      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:"2rem",marginBottom:"2rem"}}>
        <div>
          <div className="detail-card" style={{background:"linear-gradient(135deg, var(--primary-dark), var(--primary))",color:"white"}}>
            <h3 style={{color:"white",marginBottom:"1rem"}}><i className="fas fa-clipboard-list" style={{marginRight:"0.5rem"}}></i>Offering overview</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1.5rem"}}>
              <div>
                <label style={{color:"rgba(255,255,255,0.7)",fontSize:"0.75rem",textTransform:"uppercase"}}>Property manager / lister</label>
                <value style={{display:"block",fontWeight:700,fontSize:"1.1rem",marginTop:"0.3rem"}}>{formatOfferingField(propertyManagerFromRecord(property).name)}</value>
                <span style={{fontSize:"0.8rem",opacity:0.8}}>License / reference: {formatOfferingField(propertyManagerFromRecord(property).licenseReference)}</span>
              </div>
              <div>
                <label style={{color:"rgba(255,255,255,0.7)",fontSize:"0.75rem",textTransform:"uppercase"}}>Developer</label>
                <value style={{display:"block",fontWeight:700,fontSize:"1.1rem",marginTop:"0.3rem"}}>{formatOfferingField(property.developer?.name)}</value>
                <span style={{fontSize:"0.8rem",opacity:0.8}}>{formatOfferingField(property.developer?.trackRecord)}</span>
              </div>
              <div>
                <label style={{color:"rgba(255,255,255,0.7)",fontSize:"0.75rem",textTransform:"uppercase"}}>Risk Rating</label>
                <value style={{display:"block",fontWeight:700,fontSize:"1.1rem",marginTop:"0.3rem",color:property.riskRating==="Low"?"#90EE90":property.riskRating==="Medium"?"var(--accent)":property.riskRating==="High"?"var(--danger)":"inherit"}}>{formatOfferingField(property.riskRating)}</value>
              </div>
              <div>
                <label style={{color:"rgba(255,255,255,0.7)",fontSize:"0.75rem",textTransform:"uppercase"}}>Offering Type</label>
                <value style={{display:"block",fontWeight:700,fontSize:"1.1rem",marginTop:"0.3rem"}}>{formatSecOfferingType(property.secOfferingType)}</value>
              </div>
            </div>
            <div style={{marginTop:"1rem",paddingTop:"1rem",borderTop:"1px solid rgba(255,255,255,0.2)",fontSize:"0.85rem",opacity:0.9,lineHeight:1.6}}>
              <i className="fas fa-info-circle" style={{marginRight:"0.3rem"}}></i>
              {PROPERTY_OFFERING_DISCLAIMER}
            </div>
          </div>
        </div>
        <div>
          <div className="detail-card" style={{textAlign:"center"}}>
            <i className="fas fa-calendar-alt" style={{fontSize:"2rem",color:"var(--primary)",marginBottom:"0.5rem"}}></i>
            <label style={{display:"block",fontSize:"0.8rem",color:"var(--text-light)",marginBottom:"0.3rem"}}>Funding Deadline</label>
            <value style={{display:"block",fontWeight:700,fontSize:"1.3rem"}}>{formatOfferingField(property.fundingDeadline)}</value>
            <label style={{display:"block",fontSize:"0.8rem",color:"var(--text-light)",marginTop:"1rem",marginBottom:"0.3rem"}}>Projected Timeline</label>
            <value style={{display:"block",fontWeight:700,fontSize:"1.1rem"}}>{formatOfferingField(property.projectedTimeline)}</value>
          </div>
        </div>
      </div>
      )}

      <div className="detail-grid">
        <div>
          <div className="tab-nav">
            <button className={`tab-btn ${tab==="overview"?"active":""}`} onClick={()=>setTab("overview")}>Overview</button>
            {!isRentalListing && (
              <button className={`tab-btn ${tab==="financials"?"active":""}`} onClick={()=>setTab("financials")}>Financials</button>
            )}
            <button className={`tab-btn ${tab==="nearby"?"active":""}`} onClick={()=>setTab("nearby")}>Nearby</button>
            <button className={`tab-btn ${tab==="documents"?"active":""}`} onClick={()=>setTab("documents")}>Documents</button>
          </div>

          {tab === "overview" && (
            <>
              <div className="detail-card">
                <h3>Property Details</h3>
                <p style={{color:"var(--text-light)",marginBottom:"1rem"}}>{property.description}</p>
                <div className="detail-metrics">
                  <div className="detail-metric"><label>Property Type</label><value>{property.type}</value></div>
                  <div className="detail-metric"><label>Square Feet</label><value>{property.sqft ? property.sqft.toLocaleString() : "N/A"}</value></div>
                  <div className="detail-metric"><label>Bedrooms</label><value>{property.beds || "N/A"}</value></div>
                  <div className="detail-metric"><label>Bathrooms</label><value>{property.baths || "N/A"}</value></div>
                  {occupancy != null && Number.isFinite(occupancy) ? (
                    <div className="detail-metric"><label>Occupancy Rate</label><value>{occupancy}%</value></div>
                  ) : null}
                  {monthlyRent > 0 ? (
                    <div className="detail-metric"><label>Monthly Rent</label><value>{formatExactMoney(property.monthlyRent, country)}</value></div>
                  ) : null}
                  {annualExpenses != null && Number.isFinite(annualExpenses) ? (
                    <div className="detail-metric"><label>Est. annual operating expenses</label><value>{formatExactMoney(annualExpenses, country)}</value></div>
                  ) : null}
                </div>
                {property.renovation && property.renovation.needed && (
                  <div className="renovation-box">
                    <h4 style={{marginBottom:"0.5rem",display:"flex",alignItems:"center",gap:"0.5rem"}}>
                      <i className="fas fa-tools" style={{color:"var(--warning)"}}></i> Renovation Required
                    </h4>
                    <p style={{fontSize:"0.9rem",marginBottom:"0.5rem"}}>{property.renovation.description}</p>
                    <div style={{fontWeight:700,color:"var(--danger)"}}>Estimated Cost: {formatExactMoney(property.renovation.cost, country)}</div>
                  </div>
                )}
                {property.renovation && !property.renovation.needed && (
                  <div className="renovation-box renovated">
                    <h4 style={{marginBottom:"0.5rem",display:"flex",alignItems:"center",gap:"0.5rem"}}>
                      <i className="fas fa-check-circle" style={{color:"var(--success)"}}></i> No Renovation Needed
                    </h4>
                    <p style={{fontSize:"0.9rem"}}>{property.renovation.description}</p>
                  </div>
                )}
                <PropertyLocationMap location={property.location} height={260} title="Property location" />
              </div>
              {!isRentalListing && (
              <div className="detail-card">
                <h3>Investment Highlights</h3>
                <ul style={{color:"var(--text-light)",lineHeight:2,listStyle:"none"}}>
                  {projectedReturn != null ? (
                    <li><i className="fas fa-check-circle" style={{color:"var(--success)",marginRight:"0.5rem"}}></i> CoOwn reviewed projected {projectedReturn}% annual return</li>
                  ) : null}
                  <li><i className="fas fa-check-circle" style={{color:"var(--success)",marginRight:"0.5rem"}}></i> Monthly dividend distributions</li>
                  <li><i className="fas fa-check-circle" style={{color:"var(--success)",marginRight:"0.5rem"}}></i> Professional property management included</li>
                  <li><i className="fas fa-check-circle" style={{color:"var(--success)",marginRight:"0.5rem"}}></i> Sell shares anytime on marketplace</li>
                  <li><i className="fas fa-check-circle" style={{color:"var(--success)",marginRight:"0.5rem"}}></i> SEC-compliant offering structure</li>
                </ul>
              </div>
              )}
              {isRentalListing && (
              <div className="detail-card">
                <h3>Rental details</h3>
                <p style={{ color: "var(--text-light)", lineHeight: 1.7 }}>
                  This is a rental listing, not a fractional investment. Contact the listing agent to schedule a viewing or apply.
                </p>
                <RentMonthlyCostsBreakdown property={property} country={country} />
              </div>
              )}
            </>
          )}

          {tab === "financials" && !isRentalListing && (
            <div className="detail-card">
              <h3>CoOwn reviewed financials</h3>
              {!hasAdminFinancials && !annualRent ? (
                <p style={{ color: "var(--text-light)", lineHeight: 1.7 }}>
                  Financial projections are published after CoOwn admin review. Lister-provided rent and expense estimates may appear on the Overview tab.
                </p>
              ) : (
                <>
                  {hasAdminFinancials ? (
                    <>
                      <p style={{ color: "var(--text-light)", fontSize: "0.9rem", marginBottom: "1rem" }}>
                        NOI, cap rate, and return figures use CoOwn&apos;s consistent review methodology.
                      </p>
                      <div className="chart-container"><canvas ref={chartRef}></canvas></div>
                    </>
                  ) : null}
                  <div className="detail-metrics" style={{marginTop:"1rem"}}>
                    {annualRent != null ? (
                      <div className="detail-metric"><label>Gross Rent (Annual)</label><value>{formatExactMoney(annualRent, country)}</value></div>
                    ) : null}
                    {noi != null ? (
                      <div className="detail-metric"><label>Net Operating Income</label><value>{formatExactMoney(noi, country)}</value></div>
                    ) : null}
                    {capRate != null ? (
                      <div className="detail-metric"><label>Cap Rate</label><value>{formatPercent(capRate)}</value></div>
                    ) : null}
                    {projectedReturn != null ? (
                      <div className="detail-metric"><label>Cash-on-Cash Return</label><value>{formatPercent(projectedReturn)}</value></div>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "nearby" && (() => {
            const nearbyEntries = getNearbyEntries(property);
            return (
            <div className="detail-card">
              <h3>Nearby Landmarks & Amenities</h3>
              {nearbyEntries.length === 0 ? (
                <p style={{ color: "var(--text-light)", lineHeight: 1.7 }}>
                  Nearby details have not been added for this listing yet.
                </p>
              ) : (
              <div className="nearby-grid">
                {nearbyEntries.map(([key, val]) => (
                  <div key={key} className="nearby-item">
                    <i className={`fas fa-${key==="airport"?"plane":key==="train"?"train":key==="bus"?"bus":key==="supermarket"?"shopping-cart":key==="highway"?"road":key==="hospital"?"hospital":key==="school"?"graduation-cap":"map-marker-alt"}`}></i>
                    <label>{key.replace(/_/g, " ").replace(/\b\w/g, l=>l.toUpperCase())}</label>
                    <value>{val}</value>
                  </div>
                ))}
              </div>
              )}
            </div>
            );
          })()}

          {tab === "documents" && (
            <PropertyDocumentsPanel
              propertyId={property.id}
              isLoggedIn={isLoggedIn}
              addToast={addToast}
              onSignIn={onSignIn}
            />
          )}
        </div>

        <div>
          <div className="detail-card" style={{position:"sticky",top:"90px"}}>
            <h3>{isRentalListing ? "Rent this property" : "Invest Now"}</h3>
            {isRentalListing ? (
              <>
                <div className="investment-summary">
                  <div className="summary-row"><span>Monthly rent</span><span>{monthlyRent > 0 ? `${formatExactMoney(property.monthlyRent, country)}/mo` : "On request"}</span></div>
                  {getRentMonthlyCostItems(property).length > 0 && (
                    <div className="summary-row"><span>Est. total (incl. costs)</span><span>{formatExactMoney(getRentAllInMonthly(property), country)}/mo</span></div>
                  )}
                  <div className="summary-row"><span>Property type</span><span>{property.type || "—"}</span></div>
                  <div className="summary-row"><span>Bedrooms</span><span>{property.beds ?? "—"}</span></div>
                  <div className="summary-row"><span>Bathrooms</span><span>{property.baths ?? "—"}</span></div>
                </div>
                {hasListingAgent(property) ? (
                  <div style={{ marginTop: "1rem" }}>
                    <ListingAgentProfileCard property={resolveListingAgentFields(property)} onMessage={onContactAgent} compact />
                  </div>
                ) : (
                  <p style={{ fontSize: "0.85rem", color: "var(--text-light)", marginTop: "1rem" }}>
                    Agent contact is not available for this listing yet.
                  </p>
                )}
              </>
            ) : (
              <>
            {ownedSharesMessage ? (
              <div className="ownership-notice" role="status">
                <i className="fas fa-check-circle" aria-hidden="true"></i>
                <span>{ownedSharesMessage}</span>
              </div>
            ) : null}
            <div className="investment-summary">
              <div className="summary-row"><span>Property Price</span><span>{formatExactMoney(property.price, country)}</span></div>
              <div className="summary-row"><span>Share Price</span><span>{formatExactShareMoney(pricePerShare, country)}</span></div>
              <div className="summary-row"><span>Available Shares</span><span>{property.shares - sharesSold}</span></div>
              <div className="summary-row"><span>Your Shares</span><span>{myShares}</span></div>
              <div className="summary-row"><span>Funding Progress</span><span>{progress.toFixed(1)}%</span></div>
            </div>
            <div className="progress-bar" style={{marginBottom:"1.5rem"}}>
              <div className="progress-fill" style={{width:`${progress}%`}}></div>
            </div>
            {(() => {
              const minShares = Math.max(1, Math.ceil(getEffectiveMinInvestment(property) / pricePerShare - 1e-9));
              const minEstimate = estimateInvestmentTotalUsd(minShares * pricePerShare, property.country || country);
              return (
                <SaleInvestmentCostBreakdown
                  estimate={minEstimate}
                  country={country}
                  compact
                  title="Minimum investment breakdown"
                  caption={`Based on ${minShares} share${minShares !== 1 ? "s" : ""} at ${formatExactShareMoney(pricePerShare, country)} per share. Fees and tax update when you choose a share count.`}
                />
              );
            })()}
            {property.status === "funding" ? (
              <button className="btn btn-primary" style={{width:"100%",padding:"1rem"}} onClick={onInvest}>
                <i className="fas fa-chart-line"></i> Invest in This Property
              </button>
            ) : (
              <button className="btn" style={{width:"100%",padding:"1rem",background:"var(--border)",color:"var(--text-light)",cursor:"not-allowed"}} disabled>
                Fully Funded — Join Waitlist
              </button>
            )}
            <p style={{fontSize:"0.8rem",color:"var(--text-light)",marginTop:"1rem",textAlign:"center"}}>
              Minimum investment: {formatExactMoney(getEffectiveMinInvestment(property), country)} · SEC Reg CF offering
            </p>
            {hasListingAgent(property) && (
              <div style={{ marginTop: "1.25rem" }}>
                <ListingAgentProfileCard property={resolveListingAgentFields(property)} onMessage={onContactAgent} compact />
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
